export const MIN_SLOT = 1;

export interface TimedEvent {
  start: number;
  end: number;
}

export interface PositionedEvent<T extends TimedEvent> {
  event: T;
  col: number;
  cols: number;
}

export interface OverflowTile<T extends TimedEvent> {
  col: number;
  cols: number;
  start: number;
  end: number;
  hidden: T[];
}

export interface DayLayout<T extends TimedEvent> {
  tiles: PositionedEvent<T>[];
  overflows: OverflowTile<T>[];
}

interface Cluster<T extends TimedEvent> {
  events: PositionedEvent<T>[];
  cols: number;
}

function effectiveEnd(event: TimedEvent): number {
  return Math.max(event.end, event.start + MIN_SLOT);
}

export function overlaps(a: TimedEvent, b: TimedEvent): boolean {
  return a.start < effectiveEnd(b) && b.start < effectiveEnd(a);
}

function buildClusters<T extends TimedEvent>(events: T[]): Cluster<T>[] {
  const sorted = [...events].sort((a, b) => a.start - b.start || a.end - b.end);

  const clusters: Cluster<T>[] = [];
  let current: PositionedEvent<T>[] = [];
  let columnEnds: number[] = [];
  let clusterEnd = -Infinity;

  const flush = (): void => {
    if (!current.length) return;
    const cols = columnEnds.length;
    for (const positioned of current) positioned.cols = cols;
    clusters.push({ events: current, cols });
    current = [];
    columnEnds = [];
    clusterEnd = -Infinity;
  };

  for (const event of sorted) {
    if (event.start >= clusterEnd) flush();

    let col = columnEnds.findIndex((end) => event.start >= end);
    if (col === -1) {
      col = columnEnds.length;
      columnEnds.push(0);
    }
    columnEnds[col] = effectiveEnd(event);

    current.push({ event, col, cols: 0 });
    clusterEnd = Math.max(clusterEnd, effectiveEnd(event));
  }
  flush();

  return clusters;
}

export function assignEventColumns<T extends TimedEvent>(events: T[]): PositionedEvent<T>[] {
  return buildClusters(events).flatMap((cluster) => cluster.events);
}

export function layoutDay<T extends TimedEvent>(events: T[], maxCols: number): DayLayout<T> {
  const cap = Math.max(1, Math.floor(maxCols));
  const tiles: PositionedEvent<T>[] = [];
  const overflows: OverflowTile<T>[] = [];

  for (const cluster of buildClusters(events)) {
    if (cluster.cols <= cap) {
      tiles.push(...cluster.events);
      continue;
    }

    const overflowCol = cap - 1;
    const hidden: T[] = [];
    for (const positioned of cluster.events) {
      if (positioned.col < overflowCol) {
        tiles.push({ event: positioned.event, col: positioned.col, cols: cap });
      } else {
        hidden.push(positioned.event);
      }
    }

    overflows.push({
      col: overflowCol,
      cols: cap,
      start: Math.min(...hidden.map((e) => e.start)),
      end: Math.max(...hidden.map((e) => effectiveEnd(e))),
      hidden,
    });
  }

  return { tiles, overflows };
}

/**
 * When an event happens, for a one-line label, e.g. "Oct 23, 2026, 1:45 PM".
 * @param {string} day - YYYY-MM-DD
 * @param {string} startTime - HH:MM:SS
 * @returns {string}
 */
export function eventWhenLabel(day: string, startTime: string): string {
  const [y, m, d] = day.split('-').map(Number);
  const [h, min] = startTime.split(':').map(Number);
  const date = new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const period = h >= 12 ? 'PM' : 'AM';
  return `${date}, ${h % 12 || 12}:${min.toString().padStart(2, '0')} ${period}`;
}
